import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, isRef as _isRef, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "space-y-8" }
const _hoisted_2 = {
  key: 0,
  class: "py-14 text-center px-4 text-gray-500 text-lg space-y-4"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-4" }
const _hoisted_5 = { class: "text-lg font-medium" }
const _hoisted_6 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_7 = ["aria-label"]
const _hoisted_8 = { class: "whitespace-nowrap" }
const _hoisted_9 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_10 = { class: "flex items-center gap-x-0.5" }
const _hoisted_11 = { class: "flex items-center gap-x-0.5" }
const _hoisted_12 = { class: "flex items-center gap-x-0.5" }
const _hoisted_13 = { class: "flex items-center gap-x-0.5" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "min-w-36" }
const _hoisted_16 = { class: "font-medium" }
const _hoisted_17 = { class: "text-gray-500" }
const _hoisted_18 = { class: "min-w-40" }
const _hoisted_19 = { class: "min-w-48 space-y-0.5" }
const _hoisted_20 = {
  key: 0,
  class: "text-gray-400",
  "aria-hidden": "true"
}

import { computed } from 'vue';
import { getISOWeek, parseISO } from 'date-fns';
import { X } from 'lucide-vue-next';
import type { ColumnDef } from '@tanstack/vue-table';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu, DataTable, DataTableFacetFilter } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/input';
import Spinner from '@/components/ui/Spinner.vue';
import { formatQuantity, PERCENT_TONE_CLASSES, signedNumber, signTone } from '@/composables/inventory/inventoryTable';
import { drawnFromLines, type DrawnFromLine } from '@/composables/inventory/drawnFrom';
import { movementDetailsText } from '@/composables/inventory/movementDetails';
import { useInventoryFormModal } from '@/composables/inventory/useInventoryFormModal';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineMovement } from '@/types';
import DiagnosisTotals from './DiagnosisTotals.vue';
import InventoryActions from './InventoryActions.vue';
import TreatmentsChart from './log/TreatmentsChart.vue';
import { movementTypeLabel, PAGE_SIZE_OPTIONS, useMovementsPage, type MovementsPageKey } from './useMovementsPage';

type MovementColumnId = 'farm' | 'drug' | 'diagnosis' | 'details' | 'drawn_from' | 'balance_after' | 'user';

const TREATMENT_BADGE_CLASSES = 'border-transparent bg-treatment/10 text-treatment';

/** Purchases read green, spillage red, counts amber and treatments in the treatment colour. */
type PeriodZone = 'before' | 'in' | 'after';


export default /*@__PURE__*/_defineComponent({
  __name: 'MovementsTable',
  props: {
    ledgerKey: { default: null }
  },
  setup(__props: any) {

const props = __props;

const isDetail = computed(() => props.ledgerKey !== null);
const {
    diagnosisFilter,
    diagnosisOptions,
    diagnosisTotals,
    drugFilter,
    drugOptions,
    farmFilter,
    farmOptions,
    isError,
    isFetching,
    isTreatmentWeeksError,
    page,
    pageSize,
    refetch,
    resetWeek,
    rows,
    search,
    selectWeek,
    selectedWeekStart,
    sorting,
    totalPages,
    totalRows,
    netMovement,
    treatmentWeeks,
    treatmentWeeksKey,
    typeOptions,
    types,
} = useMovementsPage(computed(() => props.ledgerKey));
const { initialState, stepConfig, header, loadingKey, loadingRecordId, openMovement } = useInventoryFormModal();

const TOGGLE_COLUMN_IDS: readonly MovementColumnId[] = ['farm', 'drug', 'diagnosis', 'details', 'drawn_from', 'balance_after', 'user'];

const heading = (key: string) => i18n.global.t(`views.health.inventory.table.headings.${key}`);

/** The columns whose heading key differs from their id. */
const COLUMN_HEADING_KEYS: Partial<Record<MovementColumnId, string>> = { balance_after: 'balanceAfter', drawn_from: 'drawnFrom' };

/** A detail page is already one farm, drug and unit, so those columns would only repeat the page header; the user column is off on the Log, to fit the rest. */
const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<MovementColumnId>({
    storageKey: 'health.inventory.log.columnVisibility',
    columnIds: TOGGLE_COLUMN_IDS,
    // Which lot a movement drew from is worked out per key, so only a single key's page has it.
    isAvailable: (id) => (isDetail.value ? id !== 'farm' && id !== 'drug' : id !== 'drawn_from'),
    defaultVisible: (id) => {
        if (id === 'user') {
            return isDetail.value;
        }

        // The key's diagnosis would only repeat on every row of a detail page.
        return id !== 'diagnosis' || !isDetail.value;
    },
    label: (id) => heading(COLUMN_HEADING_KEYS[id] ?? id),
    // A hidden column has no header left to clear its filter from, so hiding it drops the filter.
    onHide: (id) => {
        if (id === 'farm') {
            farmFilter.value = [];
        } else if (id === 'drug') {
            drugFilter.value = [];
        } else if (id === 'diagnosis') {
            diagnosisFilter.value = [];
        }
    },
});

const columns = computed<ColumnDef<MedicineMovement>[]>(() => {
    const defs: ColumnDef<MedicineMovement>[] = [
        { accessorKey: 'date', header: heading('date'), enableSorting: true },
        { accessorKey: 'type', header: heading('type') },
    ];

    if (isVisible('farm')) {
        defs.push({ accessorKey: 'farm_name', header: heading('farm') });
    }
    if (isVisible('drug')) {
        defs.push({ accessorKey: 'drug_name', header: heading('drug') });
    }
    if (isVisible('diagnosis')) {
        defs.push({ accessorKey: 'diagnosis', header: heading('diagnosis') });
    }
    if (isVisible('details')) {
        defs.push({ accessorKey: 'details', header: heading('details') });
    }

    if (isVisible('drawn_from')) {
        defs.push({ accessorKey: 'drawn_from', header: heading('drawnFrom') });
    }

    defs.push({ accessorKey: 'quantity', header: heading('quantity'), enableSorting: true, meta: { align: 'right' } });

    if (isVisible('balance_after')) {
        defs.push({ accessorKey: 'balance_after', header: heading('balanceAfter'), meta: { align: 'right' } });
    }
    if (isVisible('user')) {
        defs.push({ accessorKey: 'user_name', header: heading('user'), enableSorting: true });
    }

    return defs;
});

/** The week picked on the chart, as the table header names it. */
const selectedWeekLabel = computed(() => (selectedWeekStart.value === null ? null : i18n.global.t('views.health.inventory.chart.weekFilter', { week: getISOWeek(parseISO(selectedWeekStart.value)) })));

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function detailsOf(movement: MedicineMovement): string {
    return movementDetailsText(movement, { t: (key) => i18n.global.t(key), n: formatQuantity });
}

const MOVEMENT_TYPE_CLASSES: Partial<Record<MedicineMovement['type'], string>> = {
    purchase: PERCENT_TONE_CLASSES.ok,
    treatment: TREATMENT_BADGE_CLASSES,
    retreatment: TREATMENT_BADGE_CLASSES,
    waste: PERCENT_TONE_CLASSES.error,
    count: PERCENT_TONE_CLASSES.warning,
};

const DRAWN_FROM_TONE_CLASSES: Record<DrawnFromLine['tone'], string> = { normal: '', uncovered: 'text-red-600', note: 'text-gray-500' };

function drawnFromOf(movement: MedicineMovement): DrawnFromLine[] {
    return drawnFromLines(movement, { t: (key, params) => i18n.global.t(key, params ?? {}), n: formatQuantity, date: (day) => formatDate(day) });
}

function badgeVariantOf(type: MedicineMovement['type']) {
    return MOVEMENT_TYPE_CLASSES[type] ? 'outline' : 'secondary';
}

/** The sign of a quantity gives its colour: a deduction red, an addition green; a count that found nothing missing stays neutral. */
function quantityTextOf(movement: MedicineMovement): string {
    return movement.type === 'count' && movement.quantity === 0 ? '±0' : signedNumber(movement.quantity, formatQuantity);
}

function isLoading(movement: MedicineMovement): boolean {
    return loadingKey.value === movement.key || (movement.record_id !== null && loadingRecordId.value === movement.record_id);
}

const isAnyLoading = computed(() => loadingKey.value !== null || loadingRecordId.value !== null);

function rowIdOf(movement: MedicineMovement): string {
    return movement.key;
}

/**
 * A detail page lists the key's whole history, while the figures above cover only the period: rows
 * outside it are muted, and while the list is in date order a labelled divider marks where it
 * crosses into or out of the period.
 */
const { periodRange } = useInventoryScope();

function zoneOf(movement: MedicineMovement): PeriodZone {
    const { from, to } = periodRange.value;
    const day = movement.date.slice(0, 10);

    if (from !== '' && day < from) {
        return 'before';
    }

    return to !== '' && day > to ? 'after' : 'in';
}

const PERIOD_ZONE_LABEL_KEYS: Record<PeriodZone, string> = {
    before: 'views.health.inventory.detail.zones.before',
    in: 'views.health.inventory.detail.zones.in',
    after: 'views.health.inventory.detail.zones.after',
};

/** A divider above the first row of each zone; none at the top of a page that starts inside the period, and none unless sorted by date. */
function periodDividerOf(movement: MedicineMovement, index: number): string | null {
    if (!isDetail.value || (sorting.value[0]?.id ?? 'date') !== 'date') {
        return null;
    }

    const zone = zoneOf(movement);
    const previous = index > 0 ? rows.value[index - 1] : undefined;
    const startsZone = previous ? zoneOf(previous) !== zone : zone !== 'in';

    return startsZone ? i18n.global.t(PERIOD_ZONE_LABEL_KEYS[zone]) : null;
}

/** Every row opens its movement, so every row reads as clickable. */
function rowClassOf(movement: MedicineMovement): string {
    return isDetail.value && zoneOf(movement) !== 'in' ? 'cursor-pointer opacity-60' : 'cursor-pointer';
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!isDetail.value && !_unref(isTreatmentWeeksError))
      ? (_openBlock(), _createBlock(MainViewCard, {
          key: 0,
          class: "py-4 px-2"
        }, {
          default: _withCtx(() => [
            _createVNode(TreatmentsChart, {
              weeks: _unref(treatmentWeeks),
              "scope-key": _unref(treatmentWeeksKey),
              "selected-week-start": _unref(selectedWeekStart),
              onWeekSelected: _unref(selectWeek),
              onSelectionReset: _unref(resetWeek)
            }, null, 8, ["weeks", "scope-key", "selected-week-start", "onWeekSelected", "onSelectionReset"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(MainViewCard, { class: "overflow-hidden" }, {
      default: _withCtx(() => [
        (_unref(isError))
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", null, _toDisplayString(_ctx.$t('views.pigs.records.errors.couldNotLoadData')), 1),
              _createVNode(_unref(Button), {
                variant: "outline",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(refetch)()))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.pigs.records.errors.retry')), 1)
                ]),
                _: 1
              })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("h2", _hoisted_5, _toDisplayString(_ctx.$t(isDetail.value ? 'views.health.inventory.detail.historyHeading' : 'views.health.inventory.movements.heading')), 1),
                _createElementVNode("div", _hoisted_6, [
                  (selectedWeekLabel.value)
                    ? (_openBlock(), _createBlock(_unref(Badge), {
                        key: 0,
                        variant: "outline",
                        class: "h-9 gap-x-1 rounded-lg text-normal font-normal"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(selectedWeekLabel.value) + " ", 1),
                          _createElementVNode("button", {
                            type: "button",
                            class: "rounded hover:bg-gray-100",
                            "aria-label": _ctx.$t('views.health.inventory.chart.clearWeek'),
                            onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(resetWeek) && _unref(resetWeek)(...args)))
                          }, [
                            _createVNode(_unref(X), {
                              class: "size-4",
                              "aria-hidden": "true"
                            })
                          ], 8, _hoisted_7)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(SearchInput), {
                    modelValue: _unref(search),
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(search) ? (search).value = $event : null)),
                    class: "w-64",
                    placeholder: _ctx.$t(isDetail.value ? 'views.health.inventory.movements.searchPlaceholderDetail' : 'views.health.inventory.movements.searchPlaceholder')
                  }, null, 8, ["modelValue", "placeholder"]),
                  (isDetail.value)
                    ? (_openBlock(), _createBlock(InventoryActions, {
                        key: 1,
                        prefill: _ctx.ledgerKey ?? undefined
                      }, null, 8, ["prefill"]))
                    : _createCommentVNode("", true),
                  (_unref(columnToggleOptions).length)
                    ? (_openBlock(), _createBlock(_unref(ColumnVisibilityMenu), {
                        key: 2,
                        "icon-only": "",
                        options: _unref(columnToggleOptions),
                        label: _ctx.$t('views.health.inventory.table.columnsButton'),
                        onToggle: _unref(toggleColumn),
                        onSelectAll: _cache[3] || (_cache[3] = ($event: any) => (_unref(setAllColumns)(true))),
                        onDeselectAll: _cache[4] || (_cache[4] = ($event: any) => (_unref(setAllColumns)(false)))
                      }, null, 8, ["options", "label", "onToggle"]))
                    : _createCommentVNode("", true)
                ])
              ]),
              (_unref(diagnosisTotals))
                ? (_openBlock(), _createBlock(DiagnosisTotals, {
                    key: 0,
                    totals: _unref(diagnosisTotals),
                    class: "px-4 pb-3"
                  }, null, 8, ["totals"]))
                : _createCommentVNode("", true),
              _createVNode(ActionModal, {
                "initial-step-index": 1,
                "initial-state": _unref(initialState),
                "step-config": _unref(stepConfig),
                header: _unref(header)
              }, {
                default: _withCtx(({ open }) => [
                  _createVNode(_unref(DataTable), {
                    sorting: _unref(sorting),
                    "onUpdate:sorting": _cache[9] || (_cache[9] = ($event: any) => (_isRef(sorting) ? (sorting).value = $event : null)),
                    page: _unref(page),
                    "onUpdate:page": _cache[10] || (_cache[10] = ($event: any) => (_isRef(page) ? (page).value = $event : null)),
                    "page-size": _unref(pageSize),
                    "onUpdate:pageSize": _cache[11] || (_cache[11] = ($event: any) => (_isRef(pageSize) ? (pageSize).value = $event : null)),
                    data: _unref(rows),
                    columns: columns.value,
                    loading: _unref(isFetching),
                    "page-size-options": _unref(PAGE_SIZE_OPTIONS),
                    "total-pages": _unref(totalPages),
                    "total-rows": _unref(totalRows),
                    "row-id": rowIdOf,
                    "row-class": rowClassOf,
                    "row-divider": periodDividerOf,
                    "server-side": "",
                    "row-clickable": "",
                    onRowClick: ($event: any) => (_unref(openMovement)($event, open))
                  }, _createSlots({
                    "header[type]": _withCtx(() => [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("span", null, _toDisplayString(heading('type')), 1),
                        _createVNode(_unref(DataTableFacetFilter), {
                          title: heading('type'),
                          options: _unref(typeOptions),
                          "model-value": _unref(types),
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (types.value = $event.map(String)))
                        }, null, 8, ["title", "options", "model-value"])
                      ])
                    ]),
                    "header[farm_name]": _withCtx(() => [
                      _createElementVNode("div", _hoisted_11, [
                        _createElementVNode("span", null, _toDisplayString(heading('farm')), 1),
                        _createVNode(_unref(DataTableFacetFilter), {
                          title: heading('farm'),
                          options: _unref(farmOptions),
                          "model-value": _unref(farmFilter),
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (farmFilter.value = $event.map(String)))
                        }, null, 8, ["title", "options", "model-value"])
                      ])
                    ]),
                    "header[drug_name]": _withCtx(() => [
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("span", null, _toDisplayString(heading('drug')), 1),
                        _createVNode(_unref(DataTableFacetFilter), {
                          title: heading('drug'),
                          options: _unref(drugOptions),
                          "model-value": _unref(drugFilter),
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => (drugFilter.value = $event.map(String)))
                        }, null, 8, ["title", "options", "model-value"])
                      ])
                    ]),
                    "header[diagnosis]": _withCtx(() => [
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("span", null, _toDisplayString(heading('diagnosis')), 1),
                        _createVNode(_unref(DataTableFacetFilter), {
                          title: heading('diagnosis'),
                          options: _unref(diagnosisOptions),
                          "model-value": _unref(diagnosisFilter),
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => (diagnosisFilter.value = $event.map(String)))
                        }, null, 8, ["title", "options", "model-value"])
                      ])
                    ]),
                    "item[date]": _withCtx(({ item }) => [
                      _createElementVNode("button", {
                        type: "button",
                        class: "whitespace-nowrap rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700",
                        onClick: _withModifiers(($event: any) => (_unref(openMovement)(item, open)), ["stop"])
                      }, _toDisplayString(formatDate(item.date)), 9, _hoisted_14),
                      (isLoading(item))
                        ? (_openBlock(), _createBlock(Spinner, {
                            key: 0,
                            class: "ml-2 inline h-4 w-4 align-middle",
                            "aria-hidden": "true"
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    "item[type]": _withCtx(({ item }) => [
                      _createVNode(_unref(Badge), {
                        variant: badgeVariantOf(item.type),
                        class: _normalizeClass(MOVEMENT_TYPE_CLASSES[item.type])
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(movementTypeLabel)(item.type)), 1)
                        ]),
                        _: 2
                      }, 1032, ["variant", "class"])
                    ]),
                    "item[farm_name]": _withCtx(({ item }) => [
                      _createTextVNode(_toDisplayString(item.farm_name), 1)
                    ]),
                    "item[drug_name]": _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_15, [
                        _createElementVNode("span", _hoisted_16, _toDisplayString(item.drug_name), 1),
                        _createElementVNode("span", _hoisted_17, " (" + _toDisplayString(item.unit_name) + ")", 1)
                      ])
                    ]),
                    "item[diagnosis]": _withCtx(({ item }) => [
                      _createTextVNode(_toDisplayString(item.diagnosis ?? '–'), 1)
                    ]),
                    "item[details]": _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_18, _toDisplayString(detailsOf(item)), 1)
                    ]),
                    "item[drawn_from]": _withCtx(({ item }) => [
                      _createElementVNode("div", _hoisted_19, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(drawnFromOf(item), (line) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: line.text,
                            class: _normalizeClass(DRAWN_FROM_TONE_CLASSES[line.tone])
                          }, _toDisplayString(line.text), 3))
                        }), 128)),
                        (drawnFromOf(item).length === 0)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_20, "–"))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    "item[quantity]": _withCtx(({ item }) => [
                      _createElementVNode("span", {
                        class: _normalizeClass(["whitespace-nowrap", _unref(signTone)(item.quantity)])
                      }, _toDisplayString(quantityTextOf(item)) + " " + _toDisplayString(item.unit_name), 3)
                    ]),
                    "item[balance_after]": _withCtx(({ item }) => [
                      _createElementVNode("span", {
                        class: _normalizeClass(["whitespace-nowrap", { 'text-red-600': item.balance_after < 0 }])
                      }, _toDisplayString(_unref(formatQuantity)(item.balance_after)) + " " + _toDisplayString(item.unit_name), 3)
                    ]),
                    "item[user_name]": _withCtx(({ item }) => [
                      _createTextVNode(_toDisplayString(item.user_name ?? '—'), 1)
                    ]),
                    _: 2
                  }, [
                    (!isDetail.value)
                      ? {
                          name: "totals[date]",
                          fn: _withCtx(() => [
                            _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t('views.health.inventory.movements.totalRows', { count: _ctx.$n(_unref(totalRows), 'decimal') })), 1)
                          ]),
                          key: "0"
                        }
                      : undefined,
                    (!isDetail.value)
                      ? {
                          name: "totals[quantity]",
                          fn: _withCtx(() => [
                            (_unref(netMovement))
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 0,
                                  class: _normalizeClass(["whitespace-nowrap", { 'text-red-600': _unref(netMovement).quantity < 0 }])
                                }, _toDisplayString(_unref(signedNumber)(_unref(netMovement).quantity, _unref(formatQuantity))) + " " + _toDisplayString(_unref(netMovement).unitName), 3))
                              : (_openBlock(), _createElementBlock("span", _hoisted_9, "–"))
                          ]),
                          key: "1"
                        }
                      : undefined
                  ]), 1032, ["sorting", "page", "page-size", "data", "columns", "loading", "page-size-options", "total-pages", "total-rows", "onRowClick"])
                ]),
                _: 1
              }, 8, ["initial-state", "step-config", "header"])
            ]))
      ]),
      _: 1
    })
  ]))
}
}

})